import { resolveLookupLabel } from '@/utils/product.handler'
import { isRecord } from '@/utils/typeGuards'
import type { Language } from '@/types/translations'

type ResolveLookupSources = Pick<
  Parameters<typeof resolveLookupLabel>[0],
  'references' | 'optionsByID' | 'refData'
>

export type LocalizedLookupOption = {
  id: string
  label: string
  value?: string
  key?: unknown
  options?: LocalizedLookupOption[]
  translations?: unknown
} & Partial<Record<Language, unknown>>

type LocalizedLookupSourceBase = {
  references?: ResolveLookupSources['references'] | LocalizedLookupOption[]
  optionsByID?:
    | ResolveLookupSources['optionsByID']
    | Record<string, LocalizedLookupOption>
  refData?: ResolveLookupSources['refData']
}

export type LocalizedLookupSources = LocalizedLookupSourceBase & {
  aliasLookupSources?: LocalizedLookupSourceBase[]
}

type LocalizedOptionLike = {
  id?: unknown
  key?: unknown
  label?: unknown
  value?: unknown
  options?: unknown
  translations?: unknown
} & Partial<Record<Language, unknown>>

const OPTION_LANGUAGES: readonly Language[] = [
  'FR',
  'EN',
  'DE',
  'IT',
  'NL',
  'ES',
]

const normalizeLookupToken = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number'
    ? String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .trim()
    : ''

const toOptionLanguage = (locale: string): Language | undefined => {
  const normalized = locale.toUpperCase()
  return OPTION_LANGUAGES.includes(normalized as Language)
    ? (normalized as Language)
    : undefined
}

const asLocalizedOption = (value: unknown): LocalizedOptionLike | undefined =>
  isRecord(value) ? (value as LocalizedOptionLike) : undefined

const getTranslationItems = (
  option: LocalizedOptionLike,
): Array<Record<string, unknown>> => {
  const translations = option.translations
  if (Array.isArray(translations)) {
    return translations.filter(isRecord)
  }
  if (isRecord(translations) && Array.isArray(translations.items)) {
    return translations.items.filter(isRecord)
  }
  return []
}

const getLocalizedOptionLabel = (
  option: LocalizedOptionLike | undefined,
  language: Language | undefined,
): string | undefined => {
  if (!option) return undefined

  if (language) {
    const indexedTranslation = option[language]
    if (
      isRecord(indexedTranslation) &&
      typeof indexedTranslation.label === 'string' &&
      indexedTranslation.label.trim().length > 0
    ) {
      return indexedTranslation.label.trim()
    }

    const translated = getTranslationItems(option).find((translation) => {
      const lang = translation.lang
      return typeof lang === 'string' && lang.toUpperCase() === language
    })

    if (
      translated &&
      typeof translated.label === 'string' &&
      translated.label.trim().length > 0
    ) {
      return translated.label.trim()
    }
  }

  return typeof option.label === 'string' && option.label.trim().length > 0
    ? option.label.trim()
    : undefined
}

const flattenLookupOptions = (options: unknown): LocalizedOptionLike[] => {
  if (!Array.isArray(options)) return []

  return options.flatMap((option) => {
    const current = asLocalizedOption(option)
    if (!current) return []
    return [current, ...flattenLookupOptions(current.options)]
  })
}

const optionMatchesValue = (option: LocalizedOptionLike, candidate: string) => {
  const normalizedCandidate = normalizeLookupToken(candidate)
  if (!normalizedCandidate) return false

  const tokens = [
    option.id,
    option.key,
    option.value,
    option.label,
    ...getTranslationItems(option).map((translation) => translation.label),
    ...OPTION_LANGUAGES.map((language) => {
      const translation = option[language]
      return isRecord(translation) ? translation.label : undefined
    }),
  ]

  return tokens.some(
    (token) => normalizeLookupToken(token) === normalizedCandidate,
  )
}

const findLookupOption = (
  candidate: string,
  lookupSources?: LocalizedLookupSourceBase,
): LocalizedOptionLike | undefined => {
  const direct = asLocalizedOption(lookupSources?.optionsByID?.[candidate])
  if (direct) return direct

  const byID = Object.values(lookupSources?.optionsByID ?? {})
    .map(asLocalizedOption)
    .find((option): option is LocalizedOptionLike =>
      option ? optionMatchesValue(option, candidate) : false,
    )
  if (byID) return byID

  return flattenLookupOptions(lookupSources?.references).find((option) =>
    optionMatchesValue(option, candidate),
  )
}

const findLocalizedLookupOption = (
  candidate: string,
  lookupSources?: LocalizedLookupSources,
): LocalizedOptionLike | undefined => {
  const primary = findLookupOption(candidate, lookupSources)
  if (primary) return primary

  for (const aliasLookupSources of lookupSources?.aliasLookupSources ?? []) {
    const alias = findLookupOption(candidate, aliasLookupSources)
    if (!alias) continue

    const aliasId = typeof alias.id === 'string' ? alias.id : undefined
    const localizedById = aliasId
      ? findLookupOption(aliasId, lookupSources)
      : undefined
    if (localizedById) return localizedById

    return alias
  }

  return undefined
}

export const collectOptionCandidates = (value: unknown): string[] => {
  const candidates: string[] = []

  const pushCandidate = (candidate: unknown) => {
    if (Array.isArray(candidate)) {
      candidate.forEach(pushCandidate)
      return
    }

    if (typeof candidate === 'string' || typeof candidate === 'number') {
      const normalized = String(candidate).trim()
      if (normalized) candidates.push(normalized)
      return
    }

    if (!isRecord(candidate)) return
    for (const key of ['id', 'value', 'label']) {
      pushCandidate(candidate[key])
    }
  }

  pushCandidate(value)

  return Array.from(new Set(candidates))
}

export const resolveLocalizedOptionLabel = ({
  candidates,
  locale,
  lookupSources,
  fallbackValue,
}: {
  candidates: string[]
  locale: string
  lookupSources?: LocalizedLookupSources
  fallbackValue?: string
}): string | undefined => {
  const language = toOptionLanguage(locale)

  for (const candidate of candidates) {
    const option = findLocalizedLookupOption(candidate, lookupSources)
    const localizedLabel = getLocalizedOptionLabel(option, language)
    if (localizedLabel) return localizedLabel
  }

  for (const candidate of candidates) {
    const resolved = resolveLookupLabel({
      value: candidate,
      fallbackValue,
      ...lookupSources,
    })
    if (resolved) return resolved
  }

  for (const aliasLookupSources of lookupSources?.aliasLookupSources ?? []) {
    for (const candidate of candidates) {
      const aliasOption = findLookupOption(candidate, aliasLookupSources)
      if (!aliasOption) continue
      const resolved = resolveLookupLabel({
        value: candidate,
        fallbackValue,
        ...aliasLookupSources,
      })
      if (resolved) return resolved
    }
  }

  return fallbackValue
}

export const resolveLocalizedFieldLabel = ({
  field,
  locale,
  lookupSources,
  fallbackValue,
}: {
  field: unknown
  locale: string
  lookupSources?: LocalizedLookupSources
  fallbackValue?: string
}): string | undefined =>
  resolveLocalizedOptionLabel({
    candidates: collectOptionCandidates(field),
    locale,
    lookupSources,
    fallbackValue,
  })
